import { ecrireJson, lireJson, type Resultat } from './client';
import type { Role } from './auth';

/** Une invitation telle que l'écran d'administration la voit (jamais l'empreinte). */
export interface InvitationAdmin {
  readonly id: string;
  readonly email: string;
  readonly nom: string;
  readonly role: Role;
  readonly roleLibelle: string;
  readonly creeLe: string;
  readonly expireLe: string;
  readonly utiliseLe: string | null;
  /** Encore utilisable : ni servie, ni périmée. */
  readonly valide: boolean;
  readonly perimee: boolean;
  /** Mot laissé par l'administrateur, montré au destinataire sur le lien. */
  readonly message?: string | null;
}

export interface ListeInvitations {
  readonly items: readonly InvitationAdmin[];
  readonly total: number;
  /** L'état demandé, tel que le serveur l'a compris. */
  readonly etat: EtatListeInvitations;
  /** Compteurs de l'ENSEMBLE, arrêtés par le SERVEUR (ils ne dépendent pas du filtre). */
  readonly enAttente: number;
  readonly servies: number;
}

/** Les onglets de la liste : chacun est un filtre appliqué par le serveur. */
export type EtatListeInvitations = 'en_attente' | 'servies' | 'toutes';

/** Réponse de la création : le jeton et le lien ne sont rendus QU'UNE fois. */
export interface InvitationCreee {
  readonly invitation: InvitationAdmin;
  readonly jeton: string;
  readonly lien: string;
}

/** Ce que le lien d'invitation propose, avant d'être consommé. */
export interface ApercuInvitation {
  readonly emailMasque: string;
  readonly nom: string;
  readonly role: Role;
  readonly roleLibelle: string;
  readonly expireLe: string;
  /** Le mot de l'administrateur, affiché avant le choix du mot de passe. */
  readonly message?: string | null;
}

/** Les listes FERMÉES du formulaire d'invitation, servies par le domaine. */
export interface ReferencesInvitation {
  readonly roles: ReadonlyArray<{ readonly code: string; readonly libelle: string }>;
  readonly dureesJours: readonly number[];
  readonly messageMax: number;
}

export function listerInvitations(
  options: {
    etat?: EtatListeInvitations;
    fetchImpl?: typeof fetch;
    signal?: AbortSignal;
  } = {},
): Promise<Resultat<ListeInvitations>> {
  const { etat, ...reste } = options;
  const chemin = etat
    ? `/api/admin/invitations?etat=${encodeURIComponent(etat)}`
    : '/api/admin/invitations';
  return lireJson<ListeInvitations>(chemin, reste);
}

/** Ce que la modale envoie : la durée et le message sont facultatifs. */
export interface SaisieInvitation {
  readonly email: string;
  readonly role: Role;
  readonly jours?: number;
  readonly message?: string;
  /** Facultatif : à défaut, le serveur reprend la partie locale de l'adresse. */
  readonly nom?: string;
}

export function chargerReferencesInvitation(
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<ReferencesInvitation>> {
  return lireJson<ReferencesInvitation>('/api/admin/invitations/references', options);
}

export function creerInvitation(
  saisie: SaisieInvitation,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<InvitationCreee>> {
  return ecrireJson<InvitationCreee>('/api/admin/invitations', 'POST', saisie, options);
}

export function revoquerInvitation(
  id: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<null>> {
  return ecrireJson<null>(
    `/api/admin/invitations/${encodeURIComponent(id)}`,
    'DELETE',
    {},
    options,
  );
}

/**
 * « Renvoyer » une invitation : même invitation, jeton NEUF.
 *
 * L'ancien lien cesse aussitôt de fonctionner — c'est le seul moyen de rendre
 * un lien perdu, la base ne gardant que l'empreinte.
 */
export interface ResultatLotInvitations {
  readonly id: string;
  readonly ok: boolean;
  readonly message?: string;
}

export interface ReponseLotInvitations {
  readonly resultats: readonly ResultatLotInvitations[];
  readonly faits: number;
  readonly refuses: number;
}

/** Révoque PLUSIEURS invitations en un appel : le serveur rend le sort de chacune. */
export function revoquerInvitations(
  ids: readonly string[],
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<ReponseLotInvitations>> {
  return ecrireJson<ReponseLotInvitations>(
    '/api/admin/invitations/revocations',
    'POST',
    { ids },
    options,
  );
}

export function renouvelerInvitation(
  id: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<InvitationCreee>> {
  return ecrireJson<InvitationCreee>(
    `/api/admin/invitations/${encodeURIComponent(id)}/renouvellement`,
    'POST',
    {},
    options,
  );
}

/** Vérifie le lien SANS le consommer (public). */
export function verifierInvitation(
  jeton: string,
  options: { fetchImpl?: typeof fetch; signal?: AbortSignal } = {},
): Promise<Resultat<ApercuInvitation>> {
  return lireJson<ApercuInvitation>(`/api/auth/invitation/${encodeURIComponent(jeton)}`, options);
}

/** Accepte l'invitation : crée le compte et ouvre la session (public). */
export function accepterInvitation(
  jeton: string,
  motDePasse: string,
  options: { fetchImpl?: typeof fetch } = {},
): Promise<Resultat<{ utilisateur: unknown }>> {
  return ecrireJson<{ utilisateur: unknown }>(
    `/api/auth/invitation/${encodeURIComponent(jeton)}`,
    'POST',
    { mot_de_passe: motDePasse },
    options,
  );
}
